import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { RagBadge } from "@/components/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, XCircle, AlertTriangle, ArrowRight, Building2 } from "lucide-react";
import { toast } from "sonner";

const RAG_COPY = {
  green: { title: "Fully compliant", text: "All mandatory documents are approved and current. You are cleared to carry TAG Xpress freight." },
  amber: { title: "Attention needed", text: "Some documents are awaiting review or expire within 30 days. Act soon to stay compliant." },
  red: { title: "Non-compliant", text: "Mandatory documents are missing, rejected or expired. You cannot be allocated freight until this is resolved." },
};

const RAG_BORDER = { green: "border-l-rag-green", amber: "border-l-rag-amber", red: "border-l-rag-red" };

function StatCard({ icon: Icon, label, value, tone, testid }) {
  return (
    <Card className="animate-fade-up">
      <CardContent className="flex items-center gap-4 p-5">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${tone}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="font-heading text-2xl font-extrabold text-navy" data-testid={testid}>{value}</div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SubDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => {
        setError(formatApiError(e));
        toast.error("Could not load dashboard");
      });
  }, []);

  if (error) return <div data-testid="dashboard-error" className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>;
  if (!data) return <div data-testid="dashboard-loading" className="text-sm text-muted-foreground">Loading dashboard…</div>;

  const { user, compliance, counts, profile_complete, history } = data;
  const rag = RAG_COPY[compliance.status];
  const issues = [...compliance.missing, ...compliance.rejected, ...compliance.expired];

  return (
    <div className="space-y-6" data-testid="subcontractor-dashboard">
      <div>
        <h1 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">
          G&apos;day, {user.business_name}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Your compliance overview with TAG Xpress.</p>
      </div>

      {user.suspended && (
        <div data-testid="suspended-alert" className="rounded-md border border-red-300 bg-red-50 p-4 text-sm font-medium text-red-800">
          Your account is currently suspended. Document uploads are disabled — please contact TAG Xpress.
        </div>
      )}

      {!profile_complete && (
        <div className="flex flex-col gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="profile-incomplete-alert">
          <div className="flex items-center gap-3 text-sm font-medium text-amber-800">
            <Building2 className="h-5 w-5 shrink-0" />
            Your company profile is incomplete. Complete it so TAG Xpress can verify your business.
          </div>
          <Button asChild size="sm" data-testid="complete-profile-button" className="bg-brand text-white hover:bg-brand-dark">
            <Link to="/profile">Complete profile <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </div>
      )}

      <Card className={`animate-fade-up border-l-4 ${RAG_BORDER[compliance.status]}`} data-testid="compliance-status-card">
        <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="font-heading text-xl font-extrabold text-navy">{rag.title}</h2>
              <RagBadge status={compliance.status} testid="compliance-rag-badge" />
            </div>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{rag.text}</p>
            {issues.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="compliance-issues">
                {issues.map((i) => (
                  <span key={i} className="rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">{i}</span>
                ))}
              </div>
            )}
          </div>
          <Button asChild data-testid="manage-documents-button" className="shrink-0 bg-navy text-white hover:bg-navy-light">
            <Link to="/documents">Manage documents <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={CheckCircle2} label="Approved" value={counts.approved} tone="bg-emerald-50 text-emerald-600" testid="stat-approved" />
        <StatCard icon={Clock} label="Pending Review" value={counts.pending} tone="bg-amber-50 text-amber-600" testid="stat-pending" />
        <StatCard icon={XCircle} label="Rejected / Replace" value={counts.rejected} tone="bg-red-50 text-red-600" testid="stat-rejected" />
        <StatCard icon={AlertTriangle} label="Expiring ≤ 30 days" value={compliance.expiring_soon.length} tone="bg-orange-50 text-orange-600" testid="stat-expiring" />
      </div>

      {compliance.expiring_soon.length > 0 && (
        <Card data-testid="expiring-soon-card">
          <CardHeader><CardTitle className="text-base">Expiring soon</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {compliance.expiring_soon.map((d) => (
              <div key={d.label} className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm">
                <span className="font-medium text-amber-900">{d.label}</span>
                <span className="text-xs font-semibold text-amber-700">{d.days_left} day(s) left — expires {d.expiry_date}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card data-testid="history-card">
        <CardHeader><CardTitle className="text-base">Compliance history</CardTitle></CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">No activity yet.</p>
          ) : (
            <ol className="relative space-y-4 border-l border-border pl-5">
              {history.map((h) => (
                <li key={h.id} className="relative">
                  <span className="absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-brand bg-white" />
                  <div className="text-sm font-medium text-navy">{h.detail || h.event}</div>
                  <div className="text-xs text-muted-foreground">{new Date(h.timestamp).toLocaleString("en-AU")}</div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
